import type { Capacho, Hallazgo, Lectura, Recepcion, TipoPallet, Unidad } from '../../api';
import { HUSO } from '../../theme';

/**
 * Los recorridos guiados de la vista Operar, sin React ni red: un reductor
 * puro. La pantalla le pasa lo que leyó y lo que contestó el servidor; acá se
 * decide si la lectura vale, a qué paso se va y qué se escribe al confirmar.
 *
 * Nada de lo que pasa en este archivo escribe: la única escritura sale de
 * `accion()`, y sólo en el paso `confirmar`. Volver y Cancelar no la tocan.
 * Ver docs/flujo-operativo-codigos-de-barras.md, sección 5.
 */

export type Operacion = 'recibir' | 'ubicar' | 'mover' | 'fraccionar' | 'armar' | 'egresar';

export type Paso =
  | 'inicio'
  | 'origen'
  | 'pallet'
  | 'eleccion'
  | 'destino'
  | 'estacion'
  | 'capachos'
  | 'item'
  | 'confirmar'
  | 'hecho';

/** Los pasos de cada operación, en orden: de acá sale «Paso N de M». */
export const PASOS: Record<Operacion, Paso[]> = {
  recibir: ['pallet', 'confirmar'],
  ubicar: ['pallet', 'destino', 'confirmar'],
  mover: ['origen', 'pallet', 'eleccion', 'destino', 'confirmar'],
  fraccionar: ['origen', 'pallet', 'destino', 'confirmar'],
  armar: ['estacion', 'pallet', 'capachos'],
  egresar: ['item', 'confirmar'],
};

export const NOMBRES: Record<Operacion, string> = {
  recibir: 'Recibir',
  ubicar: 'Ubicar',
  mover: 'Mover',
  fraccionar: 'Llevar a fraccionamiento',
  armar: 'Armar capachos',
  egresar: 'Egresar',
};

export type TonoLectura = 'ok' | 'warn' | 'err';

/** Lo que se muestra de la última lectura: verde vale, rojo no, ámbar avisa. */
export type Tarjeta = {
  tono: TonoLectura;
  titulo: string;
  codigo?: string;
  lineas: string[];
  /** Operaciones que resuelven el rechazo: «Ya está ubicado» ofrece Mover. */
  ofrecer?: Operacion[];
  /** Egresar un capacho sin armar se permite, pero con un toque más: es lo que sale. */
  egresarIgual?: Salida;
};

/** En una zona de piso (`Z-…`) no hay nivel: `nivel` es null y entran todos los pallets. */
export type Lugar = { posicion: string; nivel: number | null; esEstacion: boolean; esZona?: boolean };

export type PalletLeido = {
  id: string;
  producto: string;
  tipo: TipoPallet;
  estado: string;
  lugar: Lugar | null;
  piezas: number | null;
};

export type CapachoArmar = {
  id: string;
  /** Lo que dice la etiqueta: en la parte que completa otro capacho, el de ese capacho. */
  etiqueta?: string;
  orden: string | null;
  piezas: number;
  armadoEn: string | null;
  armadoPor: string | null;
};

/** `GET /fraccionamiento/:pallet`, cada capacho con lo que agrega el contrato de Operar. */
export type CapachoDePallet = Pick<Capacho, 'id' | 'piezas' | 'estado' | 'unidad'> & {
  orden?: string | null;
  armadoEn?: string | null;
  armadoPor?: string | null;
  parteDe?: string | null;
};

export type Salida = {
  id: string;
  unidad: Unidad;
  orden: string | null;
  piezas: number | null;
  pallet: string | null;
  armadoEn: string | null;
  armadoPor: string | null;
};

export type Siguiente = { op: Operacion; texto: string };

export type Comprobante = {
  titulo: string;
  filas: [string, string][];
  siguientes: Siguiente[];
  /** El capacho o la salida que se puede volver atrás desde el comprobante. */
  deshacer?: string;
};

export type RespuestaArmado = {
  capacho: string;
  orden: string | null;
  piezas: number;
  armadoEn: string;
  armadoPor: string;
  yaArmado: boolean;
};

export type Estado = {
  op: Operacion | null;
  paso: Paso;
  /** Cómo estaba cada paso anterior: Volver es sacar el último. */
  atras: Estado[];
  /** Lo leído en este paso: la relectura no vuelve a consultar. */
  leidos: Record<string, Tarjeta>;
  tarjeta: Tarjeta | null;
  contenedor?: string;
  recepcion?: { leidos: number; pallets: number; diferencias: number };
  /**
   * Origen de Mover y Llevar; en Armar, la estación leída. En una zona de piso
   * no hay un ocupante sino varios: `ocupante` queda vacío y el pallet tiene
   * que estar en `ocupantes`.
   */
  origen?: Lugar & { ocupante: string; ocupantes?: string[] };
  pallet?: PalletLeido;
  /** Capachos aceptados contra el pallet: la OT que ya lo eligió. */
  sugerida?: { ot: string | null; capachos: number } | null;
  modo?: 'reposicionar' | 'fraccionar';
  destino?: Lugar;
  capachos?: CapachoArmar[];
  saldo?: number;
  salida?: Salida;
  comprobante?: Comprobante;
  /** Códigos ya escaneados afuera (la barra de arriba), a la espera del paso que los usa. */
  cola?: string[];
  /** El de la cola que se está consultando ahora. */
  deCola?: string;
};

export type Evento =
  | { tipo: 'iniciar'; op: Operacion; codigos?: string[] }
  /** La pantalla toma el primero de la cola para leerlo. */
  | { tipo: 'desencolar' }
  | { tipo: 'cancelar' }
  | { tipo: 'volver' }
  /** Un rechazo que no necesita al servidor, o un error de red. Con `codigo`, cuenta como leído. */
  | { tipo: 'tarjeta'; tarjeta: Tarjeta; codigo?: string }
  /** `origen`: en Egresar por `DSP-…`, el pallet que se lleva, para mostrar qué ubicación libera. */
  | { tipo: 'hallazgo'; codigo: string; h: Hallazgo; capachos?: CapachoDePallet[]; saldo?: number; origen?: Hallazgo }
  | { tipo: 'recepcion'; codigo: string; contenedor: string; lectura: Pick<Lectura, 'ok' | 'mensaje' | 'yaLeido'> & { resumen?: Pick<Recepcion, 'leidos' | 'pallets' | 'diferencias'> } }
  | { tipo: 'armado'; codigo: string; r: RespuestaArmado }
  | { tipo: 'elegir'; modo: 'reposicionar' | 'fraccionar' }
  | { tipo: 'continuar' }
  | { tipo: 'terminar'; operador: string; hora: string }
  | { tipo: 'confirmado'; operador: string; hora: string; ingresados?: number; nuevoEstado?: string; yaHabia?: boolean }
  /** `incierto`: un corte de red, no se sabe si el servidor llegó a guardar. */
  | { tipo: 'falloConfirmar'; mensaje: string; incierto?: boolean }
  | { tipo: 'deshecho'; operador: string; hora: string };

/** La escritura del paso confirmar. Es lo único que la pantalla manda a guardar. */
export type Accion =
  | { tipo: 'recibir'; contenedor: string }
  | {
      tipo: 'asignar';
      codigo: string;
      pallet: string;
      /** Null en una zona de piso. */
      nivel: number | null;
      /** Ubicar manda `{ posicion: '', nivel: null }`: sólo vale si sigue sin ubicar. */
      origen?: { posicion: string; nivel: number | null };
      motivo?: string;
    }
  | { tipo: 'egresar'; id: string };

export const inicial = (): Estado => ({ op: null, paso: 'inicio', atras: [], leidos: {}, tarjeta: null });

export const textoLugar = (l: Lugar | null | undefined) =>
  !l
    ? 'sin ubicación'
    : l.esEstacion
      ? `Estación ${l.posicion}`
      : l.esZona || l.nivel === null
        ? `Zona ${l.posicion.replace(/-\d+$/, '')}`
        : `${l.posicion} · N${l.nivel}`;

/** «11:20» de una fecha ISO, en hora de Argentina. */
export const hora = (iso: string | null | undefined) =>
  iso ? new Date(iso).toLocaleTimeString('es-AR', { timeZone: HUSO, hour: '2-digit', minute: '2-digit' }) : '';

const fechaHora = (iso: string | null | undefined) =>
  iso
    ? new Date(iso).toLocaleString('es-AR', { timeZone: HUSO, day: '2-digit', month: '2-digit', hour: '2-digit', minute: '2-digit' })
    : '';

const mismo = (a: string, b: string) => a.toUpperCase() === b.toUpperCase();

const err = (codigo: string, titulo: string, ...lineas: string[]): Tarjeta => ({ tono: 'err', titulo, codigo, lineas });
const ok = (codigo: string, titulo: string, ...lineas: string[]): Tarjeta => ({ tono: 'ok', titulo, codigo, lineas });

/** Se queda en el paso con el motivo, y el código cuenta como leído. */
function rechazar(e: Estado, codigo: string, tarjeta: Tarjeta): Estado {
  return { ...e, tarjeta, leidos: { ...e.leidos, [codigo]: tarjeta } };
}

/** Pasa al paso siguiente. Lo que había queda guardado para Volver. */
function avanzar(e: Estado, paso: Paso, cambios: Partial<Estado>, tarjeta: Tarjeta | null): Estado {
  return { ...e, ...cambios, paso, tarjeta, leidos: {}, atras: [...e.atras, { ...e, tarjeta: null, deCola: undefined }] };
}

/** En qué paso va, contando desde 1, y cuántos tiene la operación. */
export function avance(e: Estado): { n: number; m: number } {
  if (!e.op) return { n: 0, m: 0 };
  const pasos = PASOS[e.op];
  const i = pasos.indexOf(e.paso);
  return { n: e.paso === 'hecho' ? pasos.length : i + 1, m: pasos.length };
}

/** El paso espera una lectura: la pantalla muestra el campo del lector. */
export const esperaLectura = (e: Estado) => ['origen', 'pallet', 'destino', 'estacion', 'capachos', 'item'].includes(e.paso);

const fraccionando = (e: Estado) => e.op === 'fraccionar' || (e.op === 'mover' && e.modo === 'fraccionar');

/**
 * Qué pide la pantalla en cada paso. `esperada` es el código exacto cuando se
 * sabe; si no, `formato` muestra la forma de la etiqueta, nunca un código que
 * parezca uno real.
 */
export function pedido(e: Estado): { pide: string; esperada?: string; formato?: string } {
  switch (e.paso) {
    case 'origen':
      return { pide: 'Escaneá el nivel o la zona de donde sacás el pallet', formato: 'U-A1-04-2 · Z-…' };
    case 'pallet':
      if (e.op === 'recibir')
        return { pide: e.contenedor ? `Escaneá otro pallet de ${e.contenedor}` : 'Escaneá un pallet del contenedor', formato: 'PLT-… o código del cliente' };
      if (e.op === 'ubicar') return { pide: 'Escaneá el pallet a ubicar', formato: 'PLT-…' };
      return { pide: e.op === 'armar' ? 'Escaneá el pallet de la estación' : 'Escaneá el pallet', esperada: e.origen?.ocupante || undefined };
    case 'eleccion':
      return { pide: '¿Qué hacés con este pallet?' };
    case 'destino':
      return fraccionando(e)
        ? { pide: 'Escaneá la etiqueta de la estación', formato: 'E-…' }
        : { pide: 'Escaneá el nivel o la zona donde lo dejás', formato: 'U-A1-04-2 · Z-…' };
    case 'estacion':
      return { pide: 'Escaneá la etiqueta de la estación', formato: 'E-…' };
    case 'capachos':
      return { pide: 'Escaneá la etiqueta de cada capacho que llenás', formato: 'CAP-…' };
    case 'item':
      return { pide: 'Escaneá el capacho que sale, o un pallet completo', formato: 'CAP-… · DSP-… · PLT-…' };
    case 'confirmar':
      return { pide: TEXTO_CONFIRMAR[e.op ?? 'ubicar'] };
    default:
      return { pide: '' };
  }
}

export const TEXTO_CONFIRMAR: Record<Operacion, string> = {
  recibir: 'Confirmar recepción',
  ubicar: 'Confirmar ubicación',
  mover: 'Confirmar traslado',
  fraccionar: 'Confirmar traslado',
  armar: 'Terminar',
  egresar: 'Confirmar egreso',
};

/**
 * Lo que se puede rechazar sin preguntarle al servidor: la relectura y el tipo
 * de etiqueta equivocado en los pasos que esperan una ubicación. Los pasos de
 * pallet no se filtran acá: un código de cliente puede empezar con U- o E-.
 */
export function previa(e: Estado, codigo: string): Tarjeta | null {
  const antes = e.leidos[codigo];
  // El aviso de sin armar se vuelve a mostrar tal cual: con «Egresar igual».
  if (antes?.egresarIgual) return antes;
  if (antes)
    return {
      tono: antes.tono === 'err' ? 'err' : 'warn',
      titulo: 'Ya leído en este paso',
      codigo,
      lineas: [antes.titulo, ...antes.lineas, 'No se volvió a consultar.'],
    };
  // El pallet ya aceptado, leído otra vez en el paso siguiente: no es un error.
  if (e.pallet && e.paso !== 'pallet' && mismo(codigo, e.pallet.id))
    return {
      tono: 'warn',
      titulo: 'Ese es el pallet',
      codigo,
      lineas: [
        e.paso === 'capachos'
          ? 'Ahora escaneá los capachos.'
          : e.paso === 'destino' && fraccionando(e)
            ? 'Ahora escaneá la estación.'
            : 'Ahora escaneá la ubicación.',
      ],
    };
  const u = codigo.startsWith('U-');
  const est = codigo.startsWith('E-');
  const z = codigo.startsWith('Z-');
  if (e.paso === 'capachos' && !codigo.startsWith('CAP-'))
    return err(codigo, 'Se esperaba un capacho (CAP-…)', 'Escaneá la etiqueta del capacho que llenaste.');
  if (e.paso === 'estacion' && !est)
    return err(codigo, 'Se esperaba la estación', 'Escaneá la etiqueta E-… de la estación de fraccionamiento.');
  if (e.paso === 'origen' && !u && !est && !z)
    return err(codigo, 'Se esperaba una ubicación (U-… o Z-…)', 'Escaneá la etiqueta del nivel o la zona de donde sacás el pallet.');
  if (e.paso === 'destino') {
    if (fraccionando(e)) {
      if (u || z) return err(codigo, 'Se esperaba la estación', 'Para fraccionar escaneá la etiqueta E-… de la estación.');
      if (!est) return err(codigo, 'Se esperaba la estación (E-…)', 'Escaneá la etiqueta de la estación de fraccionamiento.');
    } else {
      if (est) return err(codigo, 'Es una estación de fraccionamiento', 'Para llevarlo ahí usá «Llevar a fraccionamiento».');
      if (!u && !z) return err(codigo, 'Se esperaba una ubicación (U-… o Z-…)', 'Escaneá la etiqueta del nivel o la zona donde lo dejás.');
    }
  }
  return null;
}

/** El título de un rechazo del servidor: al armar, que diga qué no se pudo. */
export const tituloRechazo = (e: Estado) => (e.paso === 'capachos' ? 'No se puede armar' : 'Rechazado');

/**
 * Una lectura en una pantalla sin campo. En el comprobante arranca otra vuelta
 * de la misma operación, y la lectura cuenta como la primera; en la elección
 * y en confirmar, avisa sin perder lo elegido.
 */
export function lecturaSinCampo(e: Estado, codigo: string): Evento | null {
  if (e.paso === 'hecho' && e.op) return { tipo: 'iniciar', op: e.op };
  if (e.paso === 'eleccion')
    return { tipo: 'tarjeta', tarjeta: { tono: 'warn', titulo: 'Elegí primero qué hacer con el pallet', codigo, lineas: ['Tocá una opción y después Continuar.'] } };
  if (e.paso === 'confirmar')
    return { tipo: 'tarjeta', tarjeta: { tono: 'warn', titulo: 'Falta confirmar', codigo, lineas: ['Tocá el botón verde para guardar, o Volver para cambiar algo.'] } };
  return null;
}

/** Qué etiqueta es, por su forma: lo que no es U-, Z-, E- ni capacho se trata como pallet. */
const clase = (codigo: string) =>
  codigo.startsWith('U-') || codigo.startsWith('Z-') ? 'ubicacion' : codigo.startsWith('E-') ? 'estacion' : codigo.startsWith('CAP-') || codigo.startsWith('DSP-') ? 'capacho' : 'pallet';

/** Las clases de etiqueta que espera cada paso. */
function esperadas(e: Estado): string[] {
  switch (e.paso) {
    case 'origen':
      return ['ubicacion', 'estacion'];
    case 'pallet':
      return ['pallet'];
    case 'destino':
      return [fraccionando(e) ? 'estacion' : 'ubicacion'];
    case 'estacion':
      return ['estacion'];
    case 'capachos':
      return ['capacho'];
    case 'item':
      return ['capacho', 'pallet'];
    default:
      return [];
  }
}

/**
 * El código de la cola que el paso actual ya puede usar. Los demás esperan:
 * en Mover el pallet aguarda a que el operario lea el origen físico.
 */
export function aLeerDeCola(e: Estado): string | null {
  const primero = e.cola?.[0];
  if (!primero || e.deCola || !esperaLectura(e)) return null;
  return esperadas(e).includes(clase(primero)) ? primero : null;
}

/** Las opciones del paso de elección. El completo no se fracciona. */
export const opciones = (e: Estado): ('reposicionar' | 'fraccionar')[] =>
  e.pallet?.tipo === 'completo' ? ['reposicionar'] : ['fraccionar', 'reposicionar'];

/**
 * Lo que queda escrito en el movimiento cuando el operario eligió otra cosa
 * que la sugerida. Sólo en Mover: en Llevar a fraccionamiento no hay elección.
 */
export function motivo(e: Estado): string | undefined {
  // Desde la estación lo normal es devolverlo al rack: no hay nada que explicar.
  if (e.op !== 'mover' || e.origen?.esEstacion) return undefined;
  const sugerido = e.sugerida ? 'fraccionar' : 'reposicionar';
  if (e.modo === sugerido) return undefined;
  if (e.modo === 'reposicionar')
    return e.sugerida?.ot ? `Reposicionado; había ${e.sugerida.ot} sugerida` : 'Reposicionado; tenía capachos aceptados';
  return 'Llevado a fraccionamiento sin OT sugerida';
}

/** La escritura que corresponde al confirmar. Fuera del paso confirmar, ninguna. */
export function accion(e: Estado): Accion | null {
  if (e.paso !== 'confirmar' || !e.op) return null;
  if (e.op === 'recibir') return e.contenedor ? { tipo: 'recibir', contenedor: e.contenedor } : null;
  if (e.op === 'egresar') return e.salida ? { tipo: 'egresar', id: e.salida.id } : null;
  if (!e.pallet || !e.destino) return null;
  const a: Accion = { tipo: 'asignar', codigo: e.destino.posicion, pallet: e.pallet.id, nivel: e.destino.nivel };
  if (e.op === 'ubicar') a.origen = { posicion: '', nivel: null };
  else if (e.origen) a.origen = { posicion: e.origen.posicion, nivel: e.origen.nivel };
  const m = motivo(e);
  if (m) a.motivo = m;
  return a;
}

/** El resumen «desde → hacia» del paso confirmar. */
export function resumen(e: Estado): [string, string][] {
  const p = e.pallet;
  switch (e.op) {
    case 'recibir':
      return [
        ['Contenedor', e.contenedor ?? '—'],
        ['Leídos', `${e.recepcion?.leidos ?? 0} de ${e.recepcion?.pallets ?? 0}`],
        ['Diferencias', String(e.recepcion?.diferencias ?? 0)],
        ['Resultado', 'Pallets disponibles, sin ubicar'],
      ];
    case 'egresar': {
      const s = e.salida;
      const filas: [string, string][] = [
        [s?.unidad === 'pallet' ? 'Salida' : 'Capacho', s?.id ?? '—'],
        ['Contenido', s?.unidad === 'pallet' ? `Pallet entero${s.pallet ? ` · ${s.pallet}` : ''}` : `${s?.piezas ?? '—'} pz`],
        ['Orden', s?.orden ?? 'sin OT'],
      ];
      if (s?.unidad === 'capacho') filas.push(['Armado', s.armadoEn ? `${s.armadoPor ?? ''} · ${hora(s.armadoEn)}` : 'no figura']);
      if (s?.unidad === 'pallet' && p?.lugar) filas.push(['Ubicación', `${textoLugar(p.lugar)} (se libera)`]);
      filas.push(['Estado', 'Aceptado → Entregado']);
      return filas;
    }
    default: {
      const filas: [string, string][] = [
        ['Pallet', p?.id ?? '—'],
        ['Producto', p?.producto ?? '—'],
        ['Desde', e.op === 'ubicar' ? 'sin ubicación' : textoLugar(e.origen)],
        ['Hacia', textoLugar(e.destino)],
      ];
      const m = motivo(e);
      if (m) filas.push(['Motivo', m]);
      return filas;
    }
  }
}

function palletDe(h: Hallazgo): PalletLeido {
  return {
    id: h.codigo,
    producto: h.titulo,
    tipo: h.tipoPallet ?? 'fraccionamiento',
    estado: h.estado ?? '',
    lugar: h.posicion ? lugarDe(h) : null,
    piezas: h.piezas ?? null,
  };
}

function lugarDe(h: Hallazgo): Lugar {
  if (h.esZona) return { posicion: h.posicion ?? '', nivel: null, esEstacion: false, esZona: true };
  return { posicion: h.posicion ?? '', nivel: h.nivel ?? 1, esEstacion: Boolean(h.esEstacion) };
}

const aceptadosDe = (capachos: CapachoDePallet[] | undefined): CapachoArmar[] =>
  (capachos ?? [])
    .filter((x) => x.estado === 'Aceptado' && x.unidad === 'capacho')
    .map((x) => ({ id: x.id, etiqueta: x.parteDe ?? x.id, orden: x.orden ?? null, piezas: x.piezas, armadoEn: x.armadoEn ?? null, armadoPor: x.armadoPor ?? null }));

/** Los capachos agrupados por OT, con cuántos van armados. */
export function porOrden(capachos: CapachoArmar[]): { orden: string | null; total: number; armados: number; faltan: string[] }[] {
  const grupos = new Map<string, { orden: string | null; total: number; armados: number; faltan: string[] }>();
  for (const x of capachos) {
    const clave = x.orden ?? '';
    const g = grupos.get(clave) ?? { orden: x.orden, total: 0, armados: 0, faltan: [] };
    g.total++;
    if (x.armadoEn) g.armados++;
    else g.faltan.push(x.etiqueta ?? x.id);
    grupos.set(clave, g);
  }
  return [...grupos.values()];
}

function alHallazgo(e: Estado, codigo: string, h: Hallazgo, capachos?: CapachoDePallet[], saldo?: number, origen?: Hallazgo): Estado {
  const no = (titulo: string, ...lineas: string[]) => rechazar(e, codigo, err(codigo, titulo, ...lineas));

  if (e.paso === 'origen' || e.paso === 'destino' || e.paso === 'estacion') {
    if (h.tipo !== 'ubicacion') return no(h.tipo === 'desconocido' ? h.titulo : 'Se esperaba una ubicación', h.detalle);
    const lugar = lugarDe(h);
    const ocupante = h.ocupante ?? null;

    if (e.paso === 'origen') {
      // La zona tiene muchos pallets: el origen es «está en esta zona», y el
      // paso del pallet comprueba que el escaneado sea uno de ellos.
      if (lugar.esZona) {
        const ocupantes = (h.ocupantes ?? []).map((o) => o.pallet);
        if (!ocupantes.length) return no(`No hay pallets registrados en ${textoLugar(lugar)}`, 'Escaneá la zona o el nivel donde está el pallet.');
        return avanzar(
          e,
          'pallet',
          { origen: { ...lugar, ocupante: '', ocupantes } },
          ok(codigo, `Origen ${textoLugar(lugar)}`, `${ocupantes.length} ${ocupantes.length === 1 ? 'pallet' : 'pallets'} en la zona`, 'Escaneá el que sacás.'),
        );
      }
      if (!ocupante) return no(`No hay pallet registrado en ${textoLugar(lugar)}`, 'Escaneá el nivel donde está el pallet.');
      return avanzar(e, 'pallet', { origen: { ...lugar, ocupante: ocupante.pallet } }, ok(codigo, `Origen ${textoLugar(lugar)}`, `Debería estar ${ocupante.pallet}`, ocupante.articulo ?? ''));
    }

    if (e.paso === 'estacion') {
      if (!lugar.esEstacion) return no('Se esperaba la estación', h.detalle);
      if (!ocupante)
        return rechazar(e, codigo, { ...err(codigo, `La estación ${lugar.posicion} está vacía`, 'Llevá primero el pallet con «Llevar a fraccionamiento».'), ofrecer: ['fraccionar'] });
      return avanzar(e, 'pallet', { origen: { ...lugar, ocupante: ocupante.pallet } }, ok(codigo, `Estación ${lugar.posicion}`, `En la estación: ${ocupante.pallet}`, 'Escaneá ese pallet para empezar.'));
    }

    // destino
    const frac = fraccionando(e);
    if (frac && !lugar.esEstacion) return no('Se esperaba la estación', 'Para fraccionar escaneá la etiqueta E-… de la estación.');
    if (!frac && lugar.esEstacion) return no('Es una estación de fraccionamiento', 'Para llevarlo ahí usá «Llevar a fraccionamiento».');
    const desde = e.pallet?.lugar;
    if (desde && desde.posicion === lugar.posicion && desde.nivel === lugar.nivel) return no('Es el mismo lugar de origen', 'Escaneá otro destino.');
    // En la zona entran todos: no hay ocupado.
    if (lugar.esZona) return avanzar(e, 'confirmar', { destino: lugar }, ok(codigo, textoLugar(lugar), 'Zona de piso: entran todos los pallets que hagan falta.'));
    if (ocupante)
      return frac
        ? no(`La estación ${lugar.posicion} está ocupada`, `Tiene ${ocupante.pallet}.`, 'Terminalo o devolvelo al rack primero.')
        : no(`${textoLugar(lugar)} está ocupado`, `Ahí está ${ocupante.pallet}.`, 'Escaneá otro nivel libre.');
    return avanzar(e, 'confirmar', { destino: lugar }, ok(codigo, `${textoLugar(lugar)} libre`));
  }

  if (e.paso === 'pallet') {
    if (h.tipo === 'recepcion') {
      if (e.op === 'recibir')
        return no(`Este pallet no es de ${e.contenedor}`, `Figura en el borrador de ${h.contenedor}.`, 'Separalo y seguí con los de este contenedor.');
      return rechazar(e, codigo, {
        ...err(codigo, 'Todavía no está recibido', `Sigue en el borrador del contenedor ${h.contenedor}.`, `Confirmá primero la recepción del contenedor ${h.contenedor}.`),
        ofrecer: ['recibir'],
      });
    }
    if (e.op === 'recibir')
      return h.tipo === 'pallet'
        ? no('Este pallet ya fue recibido', h.titulo, h.estado ?? '')
        : no('No figura en ningún borrador abierto', h.detalle);
    if (h.tipo !== 'pallet') return no(h.tipo === 'capacho' ? 'Es un capacho, no un pallet' : 'Se esperaba un pallet', h.titulo, h.detalle);

    const p = palletDe(h);
    if (p.estado === 'Despachado') return no('Este pallet ya fue despachado', h.titulo);
    const detalle = `${p.producto}${p.piezas !== null && p.tipo !== 'completo' ? ` · ${p.piezas} pz` : ''}`;

    if (e.op === 'ubicar') {
      if (p.lugar)
        return rechazar(e, codigo, { ...err(codigo, `Ya está en ${textoLugar(p.lugar)}`, 'Para cambiarlo de lugar usá «Mover».'), ofrecer: ['mover'] });
      return avanzar(e, 'destino', { pallet: p }, ok(codigo, 'Pallet listo para ubicar', detalle, 'Sin ubicación'));
    }

    const esperado = e.origen?.ocupante ?? '';
    const enOrigen = e.origen?.esZona ? (e.origen.ocupantes ?? []).some((x) => mismo(x, codigo)) : mismo(codigo, esperado);
    if (!enOrigen) {
      if (e.op === 'armar') return no('No es el pallet de la estación', `En la estación está ${esperado}.`);
      if (e.origen?.esZona)
        return no(`No está en ${textoLugar(e.origen)}`, `Figura en ${textoLugar(p.lugar)}.`, 'Verificá el pallet físico; desde acá no se corrige el origen.');
      return no('No es el pallet de este lugar', `Figura en ${textoLugar(p.lugar)}.`, 'Verificá el pallet físico; desde acá no se corrige el origen.');
    }
    if (p.tipo === 'completo' && (e.op === 'fraccionar' || e.op === 'armar'))
      return rechazar(e, codigo, {
        ...err(codigo, 'Los pallets completos no van a fraccionamiento.', 'Un pallet completo sale entero: se ubica, se mueve o se egresa.'),
        ofrecer: ['mover', 'egresar'],
      });

    const aceptados = aceptadosDe(capachos);
    if (e.op === 'armar') {
      if (!aceptados.length) return no('Este pallet no tiene capachos para armar', 'Aceptá la OT primero.');
      const hechos = aceptados.filter((x) => x.armadoEn).length;
      return avanzar(e, 'capachos', { pallet: p, capachos: aceptados, saldo }, ok(codigo, `${hechos} de ${aceptados.length} armados`, detalle));
    }

    const sugerida = p.tipo !== 'completo' && aceptados.length ? { ot: aceptados.find((x) => x.orden)?.orden ?? null, capachos: aceptados.length } : null;
    if (e.op === 'fraccionar') {
      const t = sugerida
        ? ok(codigo, 'Coincide con el origen', detalle, `${sugerida.ot ?? 'Capachos'} · ${sugerida.capachos} aceptados`)
        : { ...ok(codigo, 'Coincide con el origen', detalle, 'No tiene capachos aceptados todavía.'), tono: 'warn' as const };
      return avanzar(e, 'destino', { pallet: p, sugerida, modo: 'fraccionar' }, t);
    }
    // mover
    return avanzar(
      e,
      'eleccion',
      { pallet: p, sugerida, modo: sugerida && !e.origen?.esEstacion ? 'fraccionar' : 'reposicionar' },
      ok(codigo, 'Coincide con el origen', detalle, p.tipo === 'completo' ? 'Pallet completo: sólo se reposiciona.' : textoLugar(p.lugar)),
    );
  }

  if (e.paso === 'item') {
    let s: Salida & { estado: string; entregadoEn: string | null };
    if (h.tipo === 'capacho') {
      s = {
        id: h.codigo,
        unidad: h.unidad ?? 'capacho',
        orden: h.orden ?? null,
        piezas: h.piezas ?? null,
        pallet: h.palletOrigen ?? null,
        armadoEn: h.armadoEn ?? null,
        armadoPor: h.armadoPor ?? null,
        estado: h.estado ?? '',
        entregadoEn: h.entregadoEn ?? null,
      };
    } else if (h.tipo === 'pallet') {
      if (h.tipoPallet !== 'completo') return no('Este pallet se fracciona', 'Egresá sus capachos, no el pallet.');
      if (h.estado === 'Despachado') return rechazar(e, codigo, { ...err(codigo, 'Ya egresó', 'Este pallet ya fue despachado.'), tono: 'warn' });
      if (!h.salida) return no('Este pallet no está aceptado en ninguna OT', 'Se acepta desde la orden de trabajo.');
      s = { id: h.salida.id, unidad: 'pallet', orden: h.salida.orden, piezas: h.piezas ?? null, pallet: h.codigo, armadoEn: null, armadoPor: null, estado: 'Aceptado', entregadoEn: null };
    } else {
      return no('Se esperaba un capacho (CAP-…) o un pallet completo', h.titulo, h.detalle);
    }
    if (s.estado === 'Pedido') return no('Todavía está en Pedido', 'Primero tiene que aceptarse contra un pallet.');
    if (s.estado === 'Entregado')
      return rechazar(e, codigo, { tono: 'warn', titulo: 'Ya egresó', codigo, lineas: [`Egresó el ${fechaHora(s.entregadoEn)}`, 'Si fue un error, se deshace desde la OT.'] });
    if (s.estado !== 'Aceptado') return no(`Está en ${s.estado}`, 'Sólo egresa un capacho aceptado.');
    const salida: Salida = { id: s.id, unidad: s.unidad, orden: s.orden, piezas: s.piezas, pallet: s.pallet, armadoEn: s.armadoEn, armadoPor: s.armadoPor };
    const p = h.tipo === 'pallet' ? palletDe(h) : origen?.tipo === 'pallet' ? palletDe(origen) : undefined;
    if (salida.unidad === 'capacho' && !salida.armadoEn)
      return rechazar(e, codigo, {
        tono: 'warn',
        titulo: 'No figura como armado',
        codigo,
        lineas: ['Está aceptado pero nadie lo escaneó en la estación.', 'Podés egresarlo igual; queda el aviso.'],
        egresarIgual: salida,
      });
    return avanzar(e, 'confirmar', { salida, pallet: p }, ok(codigo, salida.unidad === 'pallet' ? `Salida ${salida.id}` : 'Capacho aceptado', salida.orden ?? 'sin OT'));
  }

  return e;
}

/** Lo que muestra el comprobante, según la operación. */
function comprobanteDe(e: Estado, ev: Extract<Evento, { tipo: 'confirmado' }>): Comprobante {
  const quien: [string, string] = ['Operario', `${ev.operador} · ${ev.hora}`];
  const p = e.pallet;
  switch (e.op) {
    case 'recibir':
      return {
        titulo: 'Recepción confirmada',
        filas: [['Contenedor', e.contenedor ?? '—'], ['Pallets', `${ev.ingresados ?? e.recepcion?.leidos ?? 0} ingresados`], ['Estado', ev.nuevoEstado ?? '—'], quien],
        siguientes: [{ op: 'ubicar', texto: 'Ubicar pallets' }],
      };
    case 'ubicar':
      return {
        titulo: 'Pallet ubicado',
        filas: [['Pallet', p?.id ?? '—'], ['Producto', p?.producto ?? '—'], ['Ubicación', textoLugar(e.destino)], quien],
        siguientes: [{ op: 'ubicar', texto: 'Ubicar otro pallet' }],
      };
    case 'egresar': {
      const s = e.salida!;
      const filas: [string, string][] = [
        [s.unidad === 'pallet' ? 'Salida' : 'Capacho', s.id],
        ['Salió', s.unidad === 'pallet' ? `Pallet entero${s.pallet ? ` ${s.pallet}` : ''}` : `${s.piezas ?? '—'} pz`],
        ['Orden', s.orden ?? 'sin OT'],
      ];
      if (s.unidad === 'pallet' && p?.lugar) filas.push(['Liberó', textoLugar(p.lugar)]);
      filas.push(['Stock', 'RDM → ZF'], quien);
      return {
        titulo: ev.yaHabia ? 'Ya había egresado' : s.unidad === 'pallet' ? 'Pallet despachado' : 'Capacho egresado',
        filas,
        siguientes: [{ op: 'egresar', texto: 'Escanear otro' }],
        // También si ya había salido: el reintento tras un corte pudo ser el que lo egresó.
        deshacer: s.id,
      };
    }
    default: {
      const alaEstacion = fraccionando(e);
      const filas: [string, string][] = [['Pallet', p?.id ?? '—'], ['Movió', `${textoLugar(e.origen)} → ${textoLugar(e.destino)}`]];
      const m = motivo(e);
      if (m) filas.push(['Motivo', m]);
      if (e.sugerida?.ot) filas.push(['OT', e.sugerida.ot]);
      filas.push(quien);
      return {
        titulo: alaEstacion ? 'Pallet en la estación' : e.origen?.esEstacion ? 'Pallet devuelto a rack' : 'Reposicionamiento confirmado',
        filas,
        siguientes: alaEstacion ? [{ op: 'armar', texto: 'Armar capachos' }] : [{ op: 'mover', texto: 'Mover otro' }],
      };
    }
  }
}

/** El código que resuelve este evento, si es la respuesta a una lectura. */
function codigoDe(ev: Evento): string | undefined {
  if (ev.tipo === 'hallazgo' || ev.tipo === 'recepcion' || ev.tipo === 'armado') return ev.codigo;
  if (ev.tipo === 'tarjeta') return ev.codigo ?? ev.tarjeta.codigo;
  return undefined;
}

export function reducir(e: Estado, ev: Evento): Estado {
  const r = reducirPaso(e, ev);
  const codigo = codigoDe(ev);
  if (!e.deCola || !codigo || !mismo(codigo, e.deCola)) return r;
  // Rechazado el de la cola: el resto ya no tiene sentido, sigue a mano.
  const rechazado = r.paso === e.paso && r.tarjeta?.tono !== 'ok';
  return { ...r, deCola: undefined, cola: rechazado ? [] : r.cola };
}

function reducirPaso(e: Estado, ev: Evento): Estado {
  switch (ev.tipo) {
    case 'iniciar':
      return { ...inicial(), op: ev.op, paso: PASOS[ev.op][0]!, cola: ev.codigos?.length ? ev.codigos : undefined };
    case 'desencolar':
      if (!aLeerDeCola(e)) return e;
      return { ...e, deCola: e.cola![0], cola: e.cola!.slice(1) };
    case 'cancelar':
      return inicial();
    case 'volver': {
      if (e.paso === 'hecho') return e;
      const previo = e.atras[e.atras.length - 1];
      // La cola no vuelve atrás: lo ya usado no se vuelve a leer solo.
      return previo ? { ...previo, cola: e.cola } : inicial();
    }
    case 'tarjeta':
      return ev.codigo ? rechazar(e, ev.codigo, ev.tarjeta) : { ...e, tarjeta: ev.tarjeta };
    case 'hallazgo':
      if (!esperaLectura(e)) return e;
      return alHallazgo(e, ev.codigo, ev.h, ev.capachos, ev.saldo, ev.origen);
    case 'recepcion': {
      if (e.op !== 'recibir' || e.paso !== 'pallet') return e;
      const { lectura } = ev;
      if (!lectura.ok) return rechazar(e, ev.codigo, err(ev.codigo, 'Rechazado', lectura.mensaje));
      const r = lectura.resumen;
      const recepcion = r ? { leidos: r.leidos, pallets: r.pallets, diferencias: r.diferencias } : e.recepcion;
      const conteo = recepcion ? `Leído ${recepcion.leidos} de ${recepcion.pallets}` : 'Leído';
      const t: Tarjeta = { tono: lectura.yaLeido ? 'warn' : 'ok', titulo: lectura.yaLeido ? 'Ya leído' : conteo, codigo: ev.codigo, lineas: [lectura.mensaje] };
      return { ...rechazar(e, ev.codigo, t), contenedor: ev.contenedor, recepcion };
    }
    case 'armado': {
      if (e.paso !== 'capachos') return e;
      const { r } = ev;
      const lista = e.capachos ?? [];
      const marca = { armadoEn: r.armadoEn, armadoPor: r.armadoPor };
      const capachos = lista.some((x) => mismo(x.id, r.capacho))
        ? lista.map((x) => (mismo(x.id, r.capacho) ? { ...x, ...marca } : x))
        : [...lista, { id: r.capacho, orden: r.orden, piezas: r.piezas, ...marca }];
      const hechos = capachos.filter((x) => x.armadoEn).length;
      const deOT = r.orden ? ` · ${r.orden}` : '';
      const t: Tarjeta = r.yaArmado
        ? { tono: 'warn', titulo: 'Ya armado', codigo: ev.codigo, lineas: [`Lo armó ${r.armadoPor} a las ${hora(r.armadoEn)}`, 'No se vuelve a registrar.'] }
        : ok(ev.codigo, `Armado · ${hechos} de ${capachos.length}${deOT}`, `${r.piezas} pz · sale de este pallet`, `Registrado: ${r.armadoPor} · ${hora(r.armadoEn)}`);
      return { ...rechazar(e, ev.codigo, t), capachos };
    }
    case 'elegir':
      if (e.paso !== 'eleccion' || !opciones(e).includes(ev.modo)) return e;
      return { ...e, modo: ev.modo };
    case 'continuar':
      if (e.paso === 'eleccion') return avanzar(e, 'destino', {}, null);
      if (e.op === 'recibir' && e.paso === 'pallet' && (e.recepcion?.leidos ?? 0) > 0) return avanzar(e, 'confirmar', {}, null);
      if (e.paso === 'item' && e.tarjeta?.egresarIgual) return avanzar(e, 'confirmar', { salida: e.tarjeta.egresarIgual, pallet: undefined }, null);
      return e;
    case 'terminar': {
      if (e.paso !== 'capachos') return e;
      const grupos = porOrden(e.capachos ?? []);
      const hechos = grupos.reduce((n, g) => n + g.armados, 0);
      const total = grupos.reduce((n, g) => n + g.total, 0);
      const faltan = grupos.flatMap((g) => g.faltan);
      const filas: [string, string][] = [['Pallet', e.pallet?.id ?? '—']];
      for (const g of grupos) filas.push([g.orden ?? 'Sin OT', `${g.armados} de ${g.total} armados`]);
      if (faltan.length) filas.push(['Faltan', faltan.join(', ')]);
      if (e.saldo !== undefined) filas.push(['Saldo libre', `${e.saldo} pz`]);
      filas.push(['Operario', `${ev.operador} · ${ev.hora}`]);
      return {
        ...e,
        paso: 'hecho',
        atras: [],
        tarjeta: faltan.length ? { tono: 'warn', titulo: `Faltan ${faltan.length} capachos por armar`, lineas: ['Se puede seguir después: lo armado queda guardado.'] } : null,
        comprobante: {
          titulo: `${hechos} de ${total} capachos armados`,
          filas,
          siguientes: [
            { op: 'egresar', texto: 'Egresar capachos' },
            { op: 'mover', texto: 'Devolver pallet a rack' },
          ],
        },
      };
    }
    case 'confirmado':
      if (e.paso !== 'confirmar') return e;
      return { ...e, paso: 'hecho', atras: [], tarjeta: null, comprobante: comprobanteDe(e, ev) };
    case 'falloConfirmar': {
      if (e.paso !== 'confirmar') return e;
      // Sin respuesta no se sabe si quedó: se queda en confirmar, y el
      // reintento re-consulta antes de dar nada por hecho.
      if (ev.incierto)
        return { ...e, tarjeta: { tono: 'err', titulo: 'No se pudo confirmar', lineas: [ev.mensaje, 'No se pudo confirmar si se guardó. Revisá o reintentá.'] } };
      const previo = e.atras[e.atras.length - 1] ?? e;
      return { ...previo, tarjeta: { tono: 'err', titulo: 'No se pudo confirmar', lineas: [ev.mensaje, 'No se guardó nada. Volvé a escanear.'] } };
    }
    case 'deshecho': {
      if (e.paso !== 'hecho' || !e.comprobante) return e;
      const completo = e.salida?.unidad === 'pallet';
      return {
        ...e,
        tarjeta: completo ? { tono: 'warn', titulo: 'Pallet devuelto, sin ubicación', lineas: ['Vuelve a Disponible, pero no a su lugar: pudo haberse ocupado.', 'Ubicalo de nuevo con «Ubicar».'] } : null,
        comprobante: {
          titulo: 'Egreso deshecho',
          filas: [
            ...e.comprobante.filas
              .filter(([k]) => k !== 'Stock' && k !== 'Operario' && k !== 'Liberó')
              .map(([k, v]): [string, string] => (k === 'Salió' ? ['Contenido', v] : [k, v])),
            ['Estado', 'Entregado → Aceptado'],
            ['Stock', 'ZF → RDM'],
            ['Corrección', `${ev.operador} · ${ev.hora}`],
          ],
          siguientes: completo ? [{ op: 'ubicar', texto: 'Ubicar ahora' }, { op: 'egresar', texto: 'Escanear otro' }] : [{ op: 'egresar', texto: 'Escanear otro' }],
        },
      };
    }
  }
}
